// What a navigable chart shows: a span of its domain, moved by the wheel,
// the hand and the overview strip, no narrower than `minSpan` and no wider
// than `maxSpan`. Pure: the components only report where and how far.

export type Span = readonly [number, number];

/** The span held inside the domain, its width inside the limits. */
export function clampSpan(
  [from, to]: Span,
  [min, max]: Span,
  minSpan: number,
  maxSpan: number,
): Span {
  const width = Math.min(Math.max(to - from, minSpan), maxSpan, max - min);
  const start = Math.min(Math.max(from, min), max - width);
  return [start, start + width];
}

/** Zoomed by `factor` (above 1 widens) around `anchor`, a fraction of the
 *  span's width the pointer sits at. A span already past a limit goes no
 *  further past it. */
export function zoomSpan(
  span: Span,
  domain: Span,
  factor: number,
  anchor: number,
  minSpan: number,
  maxSpan: number,
): Span {
  const [from, to] = span;
  const was = to - from;
  const width = Math.min(
    Math.max(was * factor, Math.min(minSpan, was)),
    Math.max(maxSpan, was),
    domain[1] - domain[0],
  );
  const at = from + was * Math.min(Math.max(anchor, 0), 1);
  return clampSpan([at - width * anchor, at - width * anchor + width], domain, width, width);
}

/** Moved by `delta` domain units, its width kept. */
export function panSpan(span: Span, domain: Span, delta: number): Span {
  const width = span[1] - span[0];
  return clampSpan([span[0] + delta, span[1] + delta], domain, width, width);
}

/** As wide as the limits and the domain allow, around the span's middle. */
export function widestSpan(span: Span, domain: Span, maxSpan: number): Span {
  const width = Math.min(maxSpan, domain[1] - domain[0]);
  const middle = (span[0] + span[1]) / 2;
  return clampSpan([middle - width / 2, middle + width / 2], domain, width, width);
}

/** A span one end of which moved, held within the limits: the moved end
 *  stops where the span reaches one, and goes no further past a limit the
 *  span was already past. A span moved whole keeps its new place. */
export function resizeSpan(prev: Span, next: Span, minSpan: number, maxSpan: number): Span {
  const fromMoved = next[0] !== prev[0];
  if (fromMoved === (next[1] !== prev[1])) return next;
  const was = prev[1] - prev[0];
  const width = Math.min(
    Math.max(next[1] - next[0], Math.min(minSpan, was)),
    Math.max(maxSpan, was),
  );
  return fromMoved ? [next[1] - width, next[1]] : [next[0], next[0] + width];
}
